import './LiftFromTvChart.scss';
import { useCallback, useMemo, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Line, LineChart, Tooltip, TooltipPayload } from 'recharts';
import { ChartXAxis, ChartYAxis, ChartGrid } from '@Components/Charts/chart-presets';
import { CompanyFiscalCal, DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { formatNumberAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { ChartContainer } from '@Components/ChartContainer';
import { ChartTooltipV2 } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { Size } from '@Components/designConstants';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton';
import { AxesSkeleton, PathSkeleton, Skeleton } from '@Components/Skeleton';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { abbreviateNumber } from '@src/utils/data';
import { DATE_GROUPING_OPTIONS, formatDateLabel } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { Brand60, Neutral500 } from '@src/utils/colors';
import { GradientRange, LiftFromTvData, LINE_STROKE_WIDTH } from './lift-from-tv-chart-constants';
import { getDataByDateGrouping } from './lift-from-tv-chart-utils';

interface LiftFromTvChartProps {
  data: LiftFromTvData | undefined;
  showControls?: boolean;
  fiscalCal: CompanyFiscalCal;
}

export const LiftFromTvChart = ({ data, fiscalCal, showControls = true }: LiftFromTvChartProps) => {
  const { liftFromTv, liftModelGradientRanges } = data || {};
  const [dateGrouping, setDateGrouping] = useState<DateGroupingKey>(DATE_GROUPING_OPTIONS[0].value);
  const [graphView, setGraphView] = useState<boolean>(true);

  const excelDownload = useCallback(() => {
    exportToExcel(liftFromTv || [], 'Lift from Linear TV');
  }, [liftFromTv]);

  const pngDownload = useCallback(async () => {
    await downloadPNG('.lift-from-tv-container', 'Lift from Linear TV');
  }, []);

  const chartData = useMemo(
    () => getDataByDateGrouping(liftFromTv ?? [], dateGrouping, fiscalCal),
    [dateGrouping, liftFromTv, fiscalCal]
  );

  const headers = useMemo<Header[]>(
    () => [
      {
        label: 'Date',
        name: 'date',
        width: 125,
        nonInteractive: true,
        renderer: item => formatDateLabel(item.date, fiscalCal, '', true) || '-'
      },
      {
        label: 'Actual KPI Volume (with TV)',
        name: 'volumeWithTv',
        flex: 1,
        renderer: item => formatNumberAsInt(item.volumeWithTv)
      },
      {
        label: 'Predicted KPI Volume (w/o TV)',
        name: 'volumeWithoutTv',
        flex: 1,
        renderer: item => formatNumberAsInt(item.volumeWithoutTv)
      }
    ],
    [fiscalCal]
  );

  return (
    <div className='lift-from-tv-container'>
      <ChartContainer
        title={
          <>
            <Dropdown
              type={DropdownToggleType.WIDGET_TITLE}
              value={dateGrouping}
              options={DATE_GROUPING_OPTIONS}
              onChange={setDateGrouping}
            />
            <div>Lift from Linear TV</div>
          </>
        }
        rightActions={
          showControls && (
            <>
              <GraphTableToggleButton
                graphView={graphView}
                setGraphView={setGraphView}
                size={Size.MD}
                widgetName='liftFromTvChart'
              />
              <DownloadDropdown
                size={Size.MD}
                onClickOptions={[excelDownload, pngDownload]}
                disabled={!data}
              />
            </>
          )
        }
      >
        <LiftFromTvBody
          data={data}
          graphView={graphView}
          chartData={chartData}
          headers={headers}
          liftModelGradientRanges={liftModelGradientRanges}
          dateGrouping={dateGrouping}
          fiscalCal={fiscalCal}
        />
      </ChartContainer>
    </div>
  );
};

interface LiftFromTvBodyProps {
  data: LiftFromTvData | undefined;
  graphView: boolean;
  chartData: ReturnType<typeof getDataByDateGrouping>;
  headers: Header[];
  liftModelGradientRanges: GradientRange[] | undefined;
  dateGrouping: DateGroupingKey;
  fiscalCal: CompanyFiscalCal;
}

const LiftFromTvBody = ({
  data,
  graphView,
  chartData,
  headers,
  liftModelGradientRanges,
  dateGrouping,
  fiscalCal
}: LiftFromTvBodyProps) => {
  if (!data) {
    return (
      <Skeleton>
        <PathSkeleton
          points={[
            [0, 0.3],
            [0.25, 0.7],
            [0.5, 0.2],
            [0.75, 0.9],
            [1, 0.5]
          ]}
          thickness={10}
        />
        <AxesSkeleton size={5} />
      </Skeleton>
    );
  }

  if (!graphView) {
    return (
      <BPMTable
        headers={headers}
        data={chartData}
        filterBar={false}
        headerHeight={30}
        bottomRowHeight={30}
      />
    );
  }

  return (
    <div className='lift-from-tv-graph-contents'>
      {chartData.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '26px'
          }}
        >
          No lift from TV data.
        </div>
      ) : (
        <div className='lift-from-tv-graph'>
          <AutoSizer
            renderProp={({ width, height }) => (
              <LineChart
                data={chartData}
                width={width}
                height={height}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              >
                <ChartGrid vertical={false} />
                <defs>
                  <linearGradient id='liftChartStrokeGradient1' x1='0%' x2='100%' y1='0%' y2='0%'>
                    {(liftModelGradientRanges || []).map(range => {
                      const { start, end, live } = range;
                      const opacity = live ? 1 : 0.3;
                      return [
                        <stop
                          key={`${start}_${end}_start`}
                          offset={`${start}%`}
                          stopColor={Brand60}
                          stopOpacity={opacity}
                        />,
                        <stop
                          key={`${start}_${end}_end`}
                          offset={`${end}%`}
                          stopColor={Brand60}
                          stopOpacity={opacity}
                        />
                      ];
                    })}
                  </linearGradient>
                  <linearGradient id='liftChartStrokeGradient2' x1='0%' x2='100%' y1='0%' y2='0%'>
                    {(liftModelGradientRanges || []).map(range => {
                      const { start, end, live } = range;
                      const opacity = live ? 1 : 0.3;
                      return [
                        <stop
                          key={`${start}_${end}_start`}
                          offset={`${start}%`}
                          stopColor={Neutral500}
                          stopOpacity={opacity}
                        />,
                        <stop
                          key={`${start}_${end}_end`}
                          offset={`${end}%`}
                          stopColor={Neutral500}
                          stopOpacity={opacity}
                        />
                      ];
                    })}
                  </linearGradient>
                </defs>
                <ChartYAxis tickFormatter={number => `${abbreviateNumber(number)}`} />
                <ChartXAxis
                  dataKey='date'
                  tickFormatter={date => formatDateLabel(date, fiscalCal, dateGrouping)}
                />
                <Line
                  dataKey='volumeWithTv'
                  type='monotone'
                  strokeWidth={LINE_STROKE_WIDTH}
                  dot={false}
                  isAnimationActive={false}
                  stroke='url(#liftChartStrokeGradient1)'
                />
                <Line
                  dataKey='volumeWithoutTv'
                  type='monotone'
                  strokeWidth={LINE_STROKE_WIDTH}
                  dot={false}
                  isAnimationActive={false}
                  stroke='url(#liftChartStrokeGradient2)'
                />
                <Tooltip
                  content={<LiftFromTvTooltip dateGrouping={dateGrouping} fiscalCal={fiscalCal} />}
                />
              </LineChart>
            )}
          />
        </div>
      )}
      <div className='right-of-chart'>
        <div className='lift-from-tv-legend'>
          <div className='legend-item'>
            <div className='line' style={{ backgroundColor: Brand60 }} />
            <div className='legend-item-label'>
              <div className='primary-text'>Actual KPI Volume</div>
              <div className='sub-text'>(with Linear TV)</div>
            </div>
          </div>

          <div className='legend-item'>
            <div className='line' style={{ backgroundColor: Neutral500 }} />
            <div className='legend-item-label'>
              <div className='primary-text'>Predicted KPI Volume</div>
              <div className='sub-text'>(without Linear TV)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface LiftFromTvTooltipProps {
  dateGrouping: DateGroupingKey;
  fiscalCal: CompanyFiscalCal;
  // recharts injects these when it renders the tooltip content
  label?: string | number;
  payload?: TooltipPayload;
}

const formatTooltipLabel = (name: unknown) => (name === 'volumeWithTv' ? 'Actual' : 'Predicted');

const getTooltipColor = (name: unknown) => (name === 'volumeWithTv' ? Brand60 : Neutral500);

const LiftFromTvTooltip = ({ label, payload, dateGrouping, fiscalCal }: LiftFromTvTooltipProps) => {
  const items = [...(payload ?? [])]
    .sort((a, b) => Math.abs(Number(b.value)) - Math.abs(Number(a.value)))
    .map(({ name, value }) => ({
      label: formatTooltipLabel(name),
      value: formatNumberAsInt(Number(value ?? 0)),
      color: getTooltipColor(name)
    }));

  return (
    <ChartTooltipV2
      headers={[formatDateLabel(String(label ?? ''), fiscalCal, dateGrouping, true)]}
      items={items}
      itemKeyShape='line'
    />
  );
};
